export function renderLineChart(container, config) {
  const d3 = window.d3;
  if (!d3 || !config.data || !config.data.labels || !config.data.series.length)
    return;

  const labels = config.data.labels;
  const seriesArray =
    config.visibleSeries && config.visibleSeries.length > 0
      ? config.data.series.filter((s) => config.visibleSeries.includes(s.name))
      : config.data.series;

  const margin = {
    top: config.title ? config.margin.top * 0.35 : config.margin.top * 0.2,
    right: config.margin.right * 0.2,
    bottom:
      config.xTitle || config.legend
        ? config.margin.bottom * 0.6
        : config.margin.bottom * 0.5,
    left: config.yTitle ? config.margin.left * 0.5 : config.margin.left * 0.3,
  };
  if (config.legend && config.xTitle) margin.bottom += 10;
  if (config.subtitle) margin.top += config.margin.top * 0.1;
  const width = 600;
  const height = 300;
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const curveMap = {
    curveLinear: d3.curveLinear,
    curveNatural: d3.curveNatural,
    curveBasis: d3.curveBasis,
    curveMonotoneX: d3.curveMonotoneX,
    curveMonotoneY: d3.curveMonotoneY,
    curveBumpX: d3.curveBumpX,
    curveBumpY: d3.curveBumpY,
    curveStep: d3.curveStep,
    curveStepBefore: d3.curveStepBefore,
    curveStepAfter: d3.curveStepAfter,
    curveCardinal: d3.curveCardinal.tension(config.lineCurveValue),
    curveBundle: d3.curveBundle.beta(config.lineCurveValue),
    curveCatmullRom: d3.curveCatmullRom.alpha(config.lineCurveValue),
  };

  // Clear previous chart
  const containerSelection = d3.select(container);
  containerSelection.selectAll('*').remove();

  const svg = containerSelection
    .append('svg')
    .attr('viewBox', [0, 0, width, height])
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  // X and Y scales
  const x = d3.scalePoint().domain(labels).range([0, innerWidth]).padding(0.5);

  const allValues = seriesArray.flatMap((s) => s.values);
  const y = d3
    .scaleLinear()
    .domain([
      config.yRangeMin ?? d3.min(allValues),
      config.yRangeMax ?? d3.max(allValues),
    ])
    .nice()
    .range([innerHeight, 0]);

  // Axes
  if (config.xAxis) {
    svg
      .append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x))
      .selectAll('text')
      .attr(
        'transform',
        `translate(${config.xLabelOffset},0)rotate(${config.xLabelAngle})`
      )
      .style('text-anchor', config.xLabelAnchor);
  }
  if (config.yAxis) {
    svg
      .append('g')
      .call(d3.axisLeft(y).ticks(config.yTicks))
      .selectAll('text')
      .attr(
        'transform',
        `translate(0,${config.yLabelOffset})rotate(${config.yLabelAngle})`
      )
      .style('text-anchor', config.yLabelAnchor);
  }

  // Grid lines
  if (config.grid) {
    svg
      .append('g')
      .attr('class', 'grid')
      .selectAll('line')
      .data(y.ticks(config.gridTicks))
      .join('line')
      .filter((d) => d !== y.domain()[0])
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#e0e0e0')
      .attr('stroke-dasharray', '2,2');
  }

  // Line generator
  const line = d3
    .line()
    .x((d, i) => x(labels[i]))
    .y((d) => y(d))
    .curve(curveMap[config.lineCurveType]);

  // Color scale for multiple lines
  const color = d3
    .scaleOrdinal(d3.schemeCategory10)
    .domain(seriesArray.map((s) => s.name));

  // Tooltip
  const tooltip = d3
    .select('body')
    .append('div')
    .style('position', 'absolute')
    .style('background', 'white')
    .style('border', '1px solid #ccc')
    .style('padding', '4px 8px')
    .style('border-radius', '4px')
    .style('pointer-events', 'none')
    .style('font-size', '12px')
    .style('display', 'none')
    .style('z-index', '9999');

  // Draw each series
  seriesArray.forEach((series) => {
    // Line
    svg
      .append('path')
      .datum(series.values)
      .attr('fill', 'none')
      .attr('stroke', color(series.name))
      .attr('stroke-width', 2)
      .attr('d', line);

    // Circles
    if (config.lineDataPoints) {
      const circles = svg
        .selectAll(`.circle-${series.name}`)
        .data(series.values)
        .join('circle')
        .attr('cx', (d, i) => x(labels[i]))
        .attr('cy', (d) => y(d))
        .attr('r', 4)
        .attr('fill', color(series.name))
        .attr('pointer-events', 'visiblePainted');

      if (config.tooltip) {
        circles
          .data(series.values)
          .on('mouseover', (event, d) =>
            tooltip.style('display', 'block').html(`${series.name}: ${d}`)
          )
          .on('mousemove', (event) =>
            tooltip
              .style('left', event.pageX + 10 + 'px')
              .style('top', event.pageY - 20 + 'px')
          )
          .on('mouseout', () => tooltip.style('display', 'none'));
      }
    }
  });

  if (config.title) {
    svg
      .append('text')
      .attr('x', innerWidth / 2)
      .attr('y', -margin.top / 2)
      .attr('text-anchor', 'middle')
      .style('font-size', '16px')
      .text(config.title);
  }

  if (config.subtitle) {
    svg
      .append('text')
      .attr('x', innerWidth / 2)
      .attr('y', -margin.top / 2 + 15)
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('fill', '#666')
      .text(config.subtitle);
  }

  if (config.xTitle) {
    svg
      .append('text')
      .attr('x', innerWidth / 2)
      .attr('y', innerHeight + margin.bottom - (config.legend ? 25 : 5))
      .attr('text-anchor', 'middle')
      .style('font-size', '10px')
      .style('fill', '#666')
      .text(config.xTitle);
  }

  if (config.yTitle) {
    svg
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerHeight / 2)
      .attr('y', -margin.left + 20)
      .attr('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('fill', '#666')
      .text(config.yTitle);
  }

  // Legend
  if (config.legend) {
    const legend = svg
      .append('g')
      .attr(
        'transform',
        `translate(0, ${innerHeight + margin.bottom - (config.xTitle ? 10 : 10)})`
      );

    seriesArray.forEach((series, i) => {
      const legendGroup = legend
        .append('g')
        .attr('transform', `translate(${i * 120}, 0)`);
      legendGroup
        .append('line')
        .attr('x1', 0)
        .attr('x2', 15)
        .attr('y1', 0)
        .attr('y2', 0)
        .attr('stroke', color(series.name))
        .attr('stroke-width', 2);
      legendGroup
        .append('circle')
        .attr('cx', 7.5)
        .attr('cy', 0)
        .attr('r', 3)
        .attr('fill', color(series.name));
      legendGroup
        .append('text')
        .attr('x', 20)
        .attr('y', 3)
        .style('font-size', '12px')
        .text(series.name);
    });
  }

  if (config.lineHoverMenu) {
    const overlay = svg
      .append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'none')
      .attr('pointer-events', 'all');

    const hoverLine = svg
      .append('line')
      .attr('stroke', '#999')
      .attr('stroke-width', 1)
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .style('opacity', 0);

    overlay
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        // find closest label index
        const distances = labels.map((l) => Math.abs(x(l) - mx));
        const i = distances.indexOf(Math.min(...distances));
        const lx = x(labels[i]);
        hoverLine.attr('x1', lx).attr('x2', lx).style('opacity', 1);

        const linesHtml = seriesArray
          .map(
            (s) =>
              `<div><span style="color:${color(s.name)}">●</span> ${s.name}: ${
                s.values[i]
              }</div>`
          )
          .join('');
        tooltip
          .style('display', 'block')
          .html(`<strong>${labels[i]}</strong>${linesHtml}`)
          .style('left', event.pageX + 10 + 'px')
          .style('top', event.pageY - 30 + 'px');
      })
      .on('mouseout', () => {
        hoverLine.style('opacity', 0);
        tooltip.style('display', 'none');
      });
  }
}
